import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link as RouterLink } from "react-router-dom";
import {
    Box,
    Container,
    Typography,
    Card,
    CardContent,
    TextField,
    Autocomplete,
    Button,
    IconButton,
    Chip,
    Stepper,
    Step,
    StepLabel,
    Divider,
    Avatar,
    Alert,
    CircularProgress,
    createFilterOptions,
} from "@mui/material";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useTranslation } from "react-i18next";
import {
    fetchAllServices,
    fetchPhoneCodes,
    createPublicAppointment,
    filterMemberVisible,
    formatPrice,
    TWO_SENSE_SHOP_ID,
} from "../api/services";
import { frostedLight } from "../utils/glass";
import VideoBackground from "../components/VideoBackground";
import {
    heroContainerSx,
    useHeroPoster,
    HERO_VIDEO_SRC,
    HERO_SCRIM,
} from "../utils/heroBackground";

const INK = "#1a1a1a";
const LOCALES = { en: "en-GB", bm: "ms-MY", cn: "zh-CN" };

/* ── Date helpers (native — no date library on this project) ── */
const startOfDay = (d) => {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
};
const sameDay = (a, b) => a && b && startOfDay(a).getTime() === startOfDay(b).getTime();
const isPast = (d) => startOfDay(d).getTime() < startOfDay(new Date()).getTime();

/* Leading blanks for the first weekday + every day of the month. */
const monthGrid = (cursor) => {
    const year = cursor.getFullYear();
    const month = cursor.getMonth();
    const firstWeekday = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < firstWeekday; i += 1) cells.push(null);
    for (let day = 1; day <= daysInMonth; day += 1) cells.push(new Date(year, month, day));
    return cells;
};

/* Studio hours vary by weekday: Mon–Fri 10–20 · Sat 10–19 · Sun 11–18. */
const studioHours = (date) => {
    const day = date ? date.getDay() : 1;
    if (day === 0) return { start: 11, end: 18 };
    if (day === 6) return { start: 10, end: 19 };
    return { start: 10, end: 20 };
};

/* 30-minute slots within the day's window, each tagged by period. */
const buildSlots = (date) => {
    const { start, end } = studioHours(date);
    const slots = [];
    for (let mins = start * 60; mins < end * 60; mins += 30) {
        const h = Math.floor(mins / 60);
        const m = mins % 60;
        slots.push({
            value: `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`,
            hour: h,
            minute: m,
            period: h < 12 ? "morning" : h < 17 ? "afternoon" : "evening",
        });
    }
    return slots;
};

/* Shared field chrome — rounded, soft-shadowed input matching labeau_dashboard's
   appointment fields, with the website's monochrome (ink) accent. */
const fieldSx = {
    "& .MuiOutlinedInput-root": {
        borderRadius: "12px",
        backgroundColor: "rgba(255, 255, 255, 0.85)",
        boxShadow: "0 2px 8px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.06)",
        transition: "all 0.3s ease",
        "& fieldset": {
            borderColor: "rgba(0,0,0,0.10)",
            borderWidth: "1.5px",
            transition: "all 0.4s cubic-bezier(0.4,0,0.2,1)",
        },
        "&:hover": {
            backgroundColor: "rgba(255,255,255,0.95)",
            boxShadow: "0 4px 16px rgba(0,0,0,0.08), 0 2px 4px rgba(0,0,0,0.06)",
        },
        "&:hover fieldset": { borderColor: INK, borderWidth: "2px" },
        "&.Mui-focused": {
            backgroundColor: "#fff",
            boxShadow: "0 4px 20px rgba(0,0,0,0.10), 0 2px 8px rgba(0,0,0,0.06)",
        },
        "&.Mui-focused fieldset": { borderColor: INK, borderWidth: "2px" },
    },
    "& .MuiInputLabel-root.Mui-focused": { color: INK },
    // Required-field highlight (the `error` prop). The extra `.Mui-error`
    // class out-specifies the custom borders above, so red wins.
    "& .MuiOutlinedInput-root.Mui-error fieldset": { borderColor: "#d32f2f" },
    "& .MuiOutlinedInput-root.Mui-error:hover fieldset": { borderColor: "#d32f2f", borderWidth: "2px" },
    "& .MuiOutlinedInput-root.Mui-error.Mui-focused fieldset": { borderColor: "#d32f2f", borderWidth: "2px" },
    "& .MuiInputLabel-root.Mui-error": { color: "#d32f2f" },
};

/* Dropdown surface — rounded white paper with soft shadow; options are rounded
   rows that slide right and highlight on hover (ported from labeau_dashboard's
   AutocompletePaperComponent, recoloured to the site's monochrome palette). */
const ServicePaper = React.forwardRef(function ServicePaper({ children, style, className, ...rest }, ref) {
    return (
        <div
            ref={ref}
            {...rest}
            className={`${className || ""} ts-svc-paper`}
            style={{
                ...style,
                background: "#ffffff",
                borderRadius: "14px",
                boxShadow: "0 8px 32px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.08)",
                border: "1px solid rgba(0,0,0,0.08)",
                marginTop: "8px",
                overflow: "hidden",
            }}
        >
            <style>{`
                .ts-svc-paper .MuiAutocomplete-listbox { padding: 8px; }
                .ts-svc-paper .MuiAutocomplete-option {
                    border-radius: 10px !important;
                    padding: 12px 16px !important;
                    margin: 2px 0 !important;
                    border: none !important;
                    background-color: transparent !important;
                    color: ${INK} !important;
                    font-family: "Inter", sans-serif !important;
                    font-size: 0.9rem !important;
                    font-weight: 400 !important;
                    line-height: 1.45 !important;
                    transition: all 0.2s cubic-bezier(0.4,0,0.2,1) !important;
                    cursor: pointer !important;
                }
                .ts-svc-paper .MuiAutocomplete-option:hover,
                .ts-svc-paper .MuiAutocomplete-option.Mui-focused {
                    background-color: rgba(0,0,0,0.06) !important;
                    transform: translateX(4px) !important;
                }
                .ts-svc-paper .MuiAutocomplete-option[aria-selected="true"] {
                    background-color: rgba(0,0,0,0.08) !important;
                    font-weight: 600 !important;
                }
                .ts-svc-paper .MuiAutocomplete-option[aria-selected="true"]:hover,
                .ts-svc-paper .MuiAutocomplete-option[aria-selected="true"].Mui-focused {
                    background-color: rgba(0,0,0,0.12) !important;
                }
            `}</style>
            {children}
        </div>
    );
});

/* Lets the phone-code picker be searched by country name, ISO code, or dial
   code (e.g. "Malaysia", "MY", or "+60"). */
const phoneCodeFilter = createFilterOptions({
    stringify: (o) => `${o?.country?.name || ""} ${o?.country?.code || ""} ${o?.code || ""}`,
});

const AppointmentPage = () => {
    const { t, i18n } = useTranslation();
    const [searchParams] = useSearchParams();
    const heroPoster = useHeroPoster();
    const locale = LOCALES[(i18n.language || "en").split("-")[0]] || "en-GB";

    const [services, setServices] = useState([]);
    const [cursor, setCursor] = useState(startOfDay(new Date()));
    // Default to today so today's available time slots show straight away.
    const [selectedDate, setSelectedDate] = useState(() => startOfDay(new Date()));
    const [selectedTime, setSelectedTime] = useState(null); // "HH:mm"
    const [selectedService, setSelectedService] = useState(null);
    const [name, setName] = useState("");
    const [phone, setPhone] = useState("");
    const [phoneCodes, setPhoneCodes] = useState([]);
    const [phoneCode, setPhoneCode] = useState(null);
    const [notes, setNotes] = useState("");

    const [error, setError] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState("");
    const [submitted, setSubmitted] = useState(false);

    const presetService = searchParams.get("service") || "";

    /* ── Fetch the visible service list ── */
    useEffect(() => {
        const controller = new AbortController();
        let alive = true;
        fetchAllServices({ shopId: TWO_SENSE_SHOP_ID, signal: controller.signal })
            .then((data) => {
                if (!alive) return;
                const visible = filterMemberVisible(data);
                setServices(visible);
                if (presetService) {
                    const match = visible.find((s) => s.name === presetService);
                    if (match) setSelectedService(match);
                }
            })
            .catch(() => {});
        return () => {
            alive = false;
            controller.abort();
        };
    }, [presetService]);

    /* ── Fetch dialing codes; default to Malaysia (the shop's country) ── */
    useEffect(() => {
        const controller = new AbortController();
        let alive = true;
        fetchPhoneCodes({ signal: controller.signal })
            .then((list) => {
                if (!alive) return;
                setPhoneCodes(list);
                const fallback =
                    list.find((p) => p.country?.code === "MY") ||
                    list.find((p) => p.code === "+60") ||
                    list[0] ||
                    null;
                setPhoneCode((current) => current || fallback);
            })
            .catch(() => {});
        return () => {
            alive = false;
            controller.abort();
        };
    }, []);

    const slots = useMemo(() => buildSlots(selectedDate), [selectedDate]);
    const now = new Date();
    const slotsByPeriod = (period) =>
        slots.filter((s) => {
            if (s.period !== period) return false;
            if (selectedDate && sameDay(selectedDate, now)) {
                const slotDate = new Date(selectedDate);
                slotDate.setHours(s.hour, s.minute, 0, 0);
                return slotDate > now;
            }
            return true;
        });

    /* ── Stepper progress ── */
    const stepDone = [
        Boolean(selectedDate && selectedTime),
        Boolean(selectedService),
        Boolean(name.trim() && phone.trim()),
    ];
    const activeStep = stepDone.findIndex((d) => !d) === -1 ? 3 : stepDone.findIndex((d) => !d);

    /* ── Required-field errors (shown after a failed submit; each clears as its
       field is filled). `error` is the "submit attempted with gaps" flag. ── */
    const dateTimeError = error && !(selectedDate && selectedTime);
    const serviceError = error && !selectedService;
    const nameError = error && !name.trim();
    const phoneError = error && !phone.trim();

    // Once everything required is filled, drop the error banner + highlights.
    useEffect(() => {
        if (error && selectedDate && selectedTime && selectedService && name.trim() && phone.trim()) {
            setError(false);
        }
    }, [error, selectedDate, selectedTime, selectedService, name, phone]);

    const steps = [
        { label: t("appointmentPage.steps.dateTime"), icon: <CalendarMonthOutlinedIcon sx={{ fontSize: 18 }} /> },
        { label: t("appointmentPage.steps.service"), icon: <AssignmentOutlinedIcon sx={{ fontSize: 18 }} /> },
        { label: t("appointmentPage.steps.details"), icon: <PersonOutlineOutlinedIcon sx={{ fontSize: 18 }} /> },
    ];

    /* ── Formatting ── */
    const fmtDate = (d) =>
        d
            ? new Intl.DateTimeFormat(locale, {
                  weekday: "short",
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
              }).format(d)
            : t("appointmentPage.notSelected");
    const fmtTime = (value) => {
        if (!value) return t("appointmentPage.notSelected");
        const [h, m] = value.split(":").map(Number);
        const d = new Date();
        d.setHours(h, m, 0, 0);
        return new Intl.DateTimeFormat(locale, { hour: "numeric", minute: "2-digit", hour12: true }).format(d);
    };
    const monthLabel = new Intl.DateTimeFormat(locale, { month: "long", year: "numeric" }).format(cursor);

    /* ── Submit → backend creates the pending appointment; the system sends
       the member a WhatsApp confirmation. No customer-to-shop messaging. ── */
    const handleSubmit = async () => {
        if (!(selectedDate && selectedTime && selectedService && name.trim() && phone.trim())) {
            setError(true);
            return;
        }
        setError(false);
        setSubmitError("");
        setSubmitting(true);

        // Local selected day+time → a UTC instant for the backend; the local
        // "YYYY-MM-DD" is sent too so the dashboard groups it on the right day.
        const [h, m] = selectedTime.split(":").map(Number);
        const startLocal = new Date(
            selectedDate.getFullYear(),
            selectedDate.getMonth(),
            selectedDate.getDate(),
            h,
            m,
            0,
            0,
        );
        const pad = (n) => String(n).padStart(2, "0");
        const dateStr = `${selectedDate.getFullYear()}-${pad(selectedDate.getMonth() + 1)}-${pad(
            selectedDate.getDate(),
        )}`;

        const payload = {
            shop: TWO_SENSE_SHOP_ID,
            service_ids: [selectedService.id],
            name: name.trim(),
            phone_code: phoneCode?.id || null,
            contact: phone.trim(),
            date: dateStr,
            start: startLocal.toISOString(),
            remark: notes.trim(),
        };

        try {
            await createPublicAppointment(payload);
            setSubmitted(true);
            window.scrollTo({ top: 0, behavior: "smooth" });
        } catch (err) {
            setSubmitError(err?.message || t("appointmentPage.submitError"));
        } finally {
            setSubmitting(false);
        }
    };

    const reset = () => {
        setSubmitted(false);
        setError(false);
        setSelectedDate(startOfDay(new Date()));
        setSelectedTime(null);
        setSelectedService(null);
        setName("");
        setPhone("");
        setNotes("");
    };

    /* ── Shared card chrome ── */
    const cardSx = {
        ...frostedLight,
        borderRadius: "18px",
        mb: { xs: 2.5, md: 3 },
        overflow: "hidden",
    };
    const IconBadge = ({ children }) => (
        <Avatar variant="rounded" sx={{ width: 36, height: 36, bgcolor: INK, borderRadius: "10px", color: "#fff" }}>
            {children}
        </Avatar>
    );
    const SectionTitle = ({ icon, children }) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
            <IconBadge>{icon}</IconBadge>
            <Typography sx={{ fontFamily: '"Cormorant Garamond", serif', fontSize: "1.35rem", fontWeight: 500, color: INK }}>
                {children}
            </Typography>
        </Box>
    );

    const weekdayInitials = ["S", "M", "T", "W", "T", "F", "S"];

    return (
        <Box
            sx={{
                minHeight: "100vh",
                background:
                    "linear-gradient(180deg, #ffffff 0%, #fdfbf8 35%, #f8f3ed 70%, #f3ece2 100%)",
            }}
        >
            {/* ═══ Slim header band (keeps the floating nav readable) ═══ */}
            <Box
                sx={{
                    ...heroContainerSx,
                    pt: { xs: "7.5rem", md: "9.5rem" },
                    pb: { xs: "2.5rem", md: "3.5rem" },
                    color: "#fff",
                    textAlign: "center",
                }}
            >
                <VideoBackground
                    src={HERO_VIDEO_SRC}
                    poster={heroPoster}
                    overlay={HERO_SCRIM}
                    sx={{ zIndex: 0 }}
                />
                <Box sx={{ position: "relative", zIndex: 2 }}>
                    <Typography sx={{ fontSize: { xs: "0.62rem", md: "0.7rem" }, fontWeight: 500, letterSpacing: "4px", textTransform: "uppercase", mb: 1.5 }}>
                        {t("appointmentPage.badge")}
                    </Typography>
                    <Typography
                        variant="h1"
                        sx={{
                            fontFamily: '"Cormorant Garamond", Georgia, serif',
                            fontWeight: 400,
                            fontSize: { xs: "2.1rem", sm: "2.8rem", md: "3.4rem" },
                            lineHeight: 1.1,
                            letterSpacing: "-0.5px",
                        }}
                    >
                        {t("appointmentPage.title")}
                    </Typography>
                </Box>
            </Box>

            <Container maxWidth="sm" sx={{ px: { xs: 2, md: 3 }, py: { xs: 3, md: 5 } }}>
                {submitted ? (
                    /* ═══ Pending-confirmation panel ═══ */
                    <Card sx={cardSx}>
                        <CardContent sx={{ p: { xs: "2.5rem 1.5rem", md: "3.5rem 3rem" }, textAlign: "center" }}>
                            <CheckCircleOutlineIcon sx={{ fontSize: "3rem", color: INK, mb: 2 }} />
                            <Typography sx={{ fontSize: "0.62rem", fontWeight: 500, letterSpacing: "3px", textTransform: "uppercase", color: "rgba(0,0,0,0.5)", mb: 1.5 }}>
                                {t("appointmentPage.success.badge")}
                            </Typography>
                            <Typography sx={{ fontFamily: '"Cormorant Garamond", serif', fontSize: { xs: "1.7rem", md: "2.1rem" }, fontWeight: 500, color: INK, lineHeight: 1.2, mb: 2 }}>
                                {t("appointmentPage.success.title")}
                            </Typography>
                            <Box sx={{ width: 32, height: "1px", background: INK, mx: "auto", mb: 3 }} />
                            <Typography sx={{ fontSize: "0.95rem", color: "#666", lineHeight: 1.85, fontWeight: 300, mb: 4 }}>
                                {t("appointmentPage.success.description", { name: name.trim() })}
                            </Typography>
                            <Box>
                                <Button onClick={reset} sx={{ textTransform: "none", color: "#666", fontFamily: '"Cormorant Garamond", serif', fontStyle: "italic", fontSize: "0.95rem", "&:hover": { color: INK, background: "transparent" } }}>
                                    {t("appointmentPage.success.again")}
                                </Button>
                            </Box>
                        </CardContent>
                    </Card>
                ) : (
                    <>
                        {/* ═══ Progress stepper ═══ */}
                        <Box sx={{ mb: { xs: 3, md: 4 } }}>
                            <Stepper activeStep={activeStep} alternativeLabel>
                                {steps.map((step, idx) => (
                                    <Step key={step.label} completed={stepDone[idx]}>
                                        <StepLabel
                                            StepIconComponent={() => (
                                                <Avatar
                                                    sx={{
                                                        width: 30,
                                                        height: 30,
                                                        bgcolor: idx <= activeStep || stepDone[idx] ? INK : "rgba(0,0,0,0.10)",
                                                        color: idx <= activeStep || stepDone[idx] ? "#fff" : "rgba(0,0,0,0.4)",
                                                        transition: "all .3s ease",
                                                    }}
                                                >
                                                    {stepDone[idx] ? <CheckCircleIcon sx={{ fontSize: 18 }} /> : React.cloneElement(step.icon, { sx: { fontSize: 16 } })}
                                                </Avatar>
                                            )}
                                            sx={{
                                                "& .MuiStepLabel-label": {
                                                    fontSize: "0.68rem",
                                                    fontWeight: 500,
                                                    letterSpacing: "0.5px",
                                                    textTransform: "uppercase",
                                                    mt: "6px !important",
                                                    color: "rgba(0,0,0,0.45)",
                                                    "&.Mui-active, &.Mui-completed": { color: INK, fontWeight: 600 },
                                                },
                                            }}
                                        >
                                            {step.label}
                                        </StepLabel>
                                    </Step>
                                ))}
                            </Stepper>
                        </Box>

                        {/* ═══ 1 · Date & Time ═══ */}
                        <Card sx={{ ...cardSx, ...(dateTimeError && { border: "1px solid #d32f2f" }) }}>
                            <CardContent sx={{ p: { xs: 2.25, md: 3 } }}>
                                <SectionTitle icon={<CalendarMonthOutlinedIcon sx={{ fontSize: 18 }} />}>
                                    {t("appointmentPage.selectDateTime")}
                                </SectionTitle>

                                {/* Month navigation */}
                                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                                    <IconButton
                                        onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}
                                        disabled={cursor.getFullYear() === new Date().getFullYear() && cursor.getMonth() === new Date().getMonth()}
                                        sx={{ border: "1px solid rgba(0,0,0,0.12)", borderRadius: "10px" }}
                                    >
                                        <ChevronLeftIcon />
                                    </IconButton>
                                    <Typography sx={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: "1px", fontSize: "0.95rem", color: INK }}>
                                        {monthLabel}
                                    </Typography>
                                    <IconButton
                                        onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}
                                        sx={{ border: "1px solid rgba(0,0,0,0.12)", borderRadius: "10px" }}
                                    >
                                        <ChevronRightIcon />
                                    </IconButton>
                                </Box>

                                {/* Weekday header */}
                                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", mb: 1 }}>
                                    {weekdayInitials.map((d, i) => (
                                        <Typography key={i} sx={{ textAlign: "center", fontSize: "0.72rem", fontWeight: 600, color: INK, py: 0.5 }}>
                                            {d}
                                        </Typography>
                                    ))}
                                </Box>

                                {/* Calendar grid */}
                                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", rowGap: 0.5 }}>
                                    {monthGrid(cursor).map((date, idx) => {
                                        if (!date) return <Box key={`e-${idx}`} />;
                                        const selected = sameDay(date, selectedDate);
                                        const today = sameDay(date, new Date());
                                        const past = isPast(date);
                                        return (
                                            <Box key={idx} sx={{ display: "flex", justifyContent: "center", py: 0.25 }}>
                                                <Button
                                                    disableRipple
                                                    disabled={past}
                                                    onClick={() => {
                                                        setSelectedDate(date);
                                                        setSelectedTime(null);
                                                    }}
                                                    sx={{
                                                        minWidth: 0,
                                                        width: 38,
                                                        height: 38,
                                                        p: 0,
                                                        borderRadius: "50%",
                                                        fontSize: "0.82rem",
                                                        fontWeight: selected ? 600 : 500,
                                                        color: selected ? "#fff" : past ? "rgba(0,0,0,0.25)" : INK,
                                                        background: selected ? INK : "transparent",
                                                        border: today && !selected ? `1.5px solid ${INK}` : "1.5px solid transparent",
                                                        "&:hover": { background: selected ? "#000" : "rgba(0,0,0,0.06)" },
                                                        "&.Mui-disabled": { color: "rgba(0,0,0,0.22)" },
                                                    }}
                                                >
                                                    {date.getDate()}
                                                </Button>
                                            </Box>
                                        );
                                    })}
                                </Box>

                                {dateTimeError && (
                                    <Typography sx={{ mt: 2, fontSize: "0.8rem", color: "#d32f2f", fontWeight: 500 }}>
                                        {t("appointmentPage.form.selectDateTimeError")}
                                    </Typography>
                                )}

                                {/* Time slots */}
                                {selectedDate && (
                                    <Box sx={{ mt: 3 }}>
                                        <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: INK, mb: 1.5 }}>
                                            {t("appointmentPage.availableTimes")}
                                        </Typography>
                                        {["morning", "afternoon", "evening"].map((period) => {
                                            const periodSlots = slotsByPeriod(period);
                                            if (periodSlots.length === 0) return null;
                                            return (
                                                <Box key={period} sx={{ mb: 2 }}>
                                                    <Typography sx={{ fontSize: "0.72rem", fontWeight: 500, color: "#888", mb: 1, textTransform: "capitalize" }}>
                                                        {t(`appointmentPage.period.${period}`)}
                                                    </Typography>
                                                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                                        {periodSlots.map((slot) => {
                                                            const active = selectedTime === slot.value;
                                                            return (
                                                                <Chip
                                                                    key={slot.value}
                                                                    label={fmtTime(slot.value)}
                                                                    clickable
                                                                    onClick={() => setSelectedTime(slot.value)}
                                                                    sx={{
                                                                        borderRadius: "10px",
                                                                        fontWeight: 600,
                                                                        fontSize: "0.75rem",
                                                                        height: 34,
                                                                        color: active ? "#fff" : INK,
                                                                        background: active ? INK : "rgba(255,255,255,0.7)",
                                                                        border: `1px solid ${active ? INK : "rgba(0,0,0,0.12)"}`,
                                                                        "&:hover": { background: active ? "#000" : "rgba(0,0,0,0.06)" },
                                                                    }}
                                                                />
                                                            );
                                                        })}
                                                    </Box>
                                                </Box>
                                            );
                                        })}
                                        {["morning", "afternoon", "evening"].every((p) => slotsByPeriod(p).length === 0) && (
                                            <Typography sx={{ fontSize: "0.85rem", color: "#999", fontStyle: "italic" }}>
                                                {t("appointmentPage.noSlots")}
                                            </Typography>
                                        )}
                                    </Box>
                                )}
                            </CardContent>
                        </Card>

                        {/* ═══ 2 · Service ═══ */}
                        <Card sx={cardSx}>
                            <CardContent sx={{ p: { xs: 2.25, md: 3 } }}>
                                <SectionTitle icon={<AssignmentOutlinedIcon sx={{ fontSize: 18 }} />}>
                                    {t("appointmentPage.steps.service")}
                                </SectionTitle>

                                <Autocomplete
                                    options={services}
                                    value={selectedService}
                                    onChange={(_e, v) => setSelectedService(v)}
                                    getOptionLabel={(o) => o?.name || ""}
                                    isOptionEqualToValue={(o, v) => o.id === v.id}
                                    PaperComponent={ServicePaper}
                                    sx={fieldSx}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            error={serviceError}
                                            label={t("appointmentPage.form.service")}
                                            placeholder={t("appointmentPage.form.servicePlaceholder")}
                                        />
                                    )}
                                />

                                {selectedService && (
                                    <Box sx={{ mt: 2.5, p: 2, borderRadius: "12px", background: "rgba(0,0,0,0.03)", border: "1px solid rgba(0,0,0,0.08)" }}>
                                        <Typography sx={{ fontFamily: '"Cormorant Garamond", serif', fontSize: "1.2rem", fontWeight: 600, color: INK, mb: 1 }}>
                                            {selectedService.name}
                                        </Typography>
                                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                            {(selectedService.shop_duration_minutes || selectedService.duration_minutes) > 0 && (
                                                <Chip
                                                    size="small"
                                                    icon={<AccessTimeOutlinedIcon sx={{ fontSize: 15 }} />}
                                                    label={`${selectedService.shop_duration_minutes || selectedService.duration_minutes} ${t("appointmentPage.minutesShort")}`}
                                                    sx={{ background: "rgba(0,0,0,0.05)", fontWeight: 600, fontSize: "0.72rem" }}
                                                />
                                            )}
                                            {(selectedService.shop_price ?? selectedService.price) != null && (
                                                <Chip
                                                    size="small"
                                                    label={`RM ${formatPrice(selectedService.shop_price ?? selectedService.price)}`}
                                                    sx={{ background: "rgba(0,0,0,0.05)", fontWeight: 600, fontSize: "0.72rem" }}
                                                />
                                            )}
                                        </Box>
                                        {selectedService.description && (
                                            <Typography sx={{ fontSize: "0.82rem", color: "#777", mt: 1.5, fontStyle: "italic", lineHeight: 1.6 }}>
                                                {selectedService.description}
                                            </Typography>
                                        )}
                                    </Box>
                                )}
                            </CardContent>
                        </Card>

                        {/* ═══ 3 · Details ═══ */}
                        <Card sx={cardSx}>
                            <CardContent sx={{ p: { xs: 2.25, md: 3 } }}>
                                <SectionTitle icon={<PersonOutlineOutlinedIcon sx={{ fontSize: 18 }} />}>
                                    {t("appointmentPage.yourInformation")}
                                </SectionTitle>

                                <TextField
                                    fullWidth
                                    error={nameError}
                                    label={t("appointmentPage.form.name")}
                                    placeholder={t("appointmentPage.form.namePlaceholder")}
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    sx={{ ...fieldSx, mb: 2.5 }}
                                />
                                <Box sx={{ display: "flex", gap: 1.5, mb: 2.5 }}>
                                    <Autocomplete
                                        options={phoneCodes}
                                        value={phoneCode}
                                        onChange={(_e, v) => {
                                            setPhoneCode(v);
                                            // Drop a leading "0" if switching to Malaysia (+60).
                                            if (v?.code === "+60") setPhone((p) => p.replace(/^0+/, ""));
                                        }}
                                        getOptionLabel={(o) => o?.code || ""}
                                        isOptionEqualToValue={(o, v) => o.id === v.id}
                                        filterOptions={phoneCodeFilter}
                                        disableClearable
                                        PaperComponent={ServicePaper}
                                        sx={{ ...fieldSx, width: { xs: 116, sm: 140 }, flexShrink: 0 }}
                                        renderOption={(props, option) => (
                                            <li {...props} key={option.id}>
                                                {option.country?.name
                                                    ? `${option.country.name} (${option.code})`
                                                    : option.code}
                                            </li>
                                        )}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label={t("appointmentPage.form.code")}
                                            />
                                        )}
                                    />
                                    <TextField
                                        fullWidth
                                        type="tel"
                                        error={phoneError}
                                        label={t("appointmentPage.form.phone")}
                                        value={phone}
                                        onChange={(e) => {
                                            // Match labeau_dashboard's IndividualInfo contact field:
                                            // digits only (strip every non-numeric character).
                                            let v = e.target.value.replace(/[^0-9]/g, "");
                                            // Malaysia (+60): international format drops the leading
                                            // trunk "0", so the number never starts with 0.
                                            if (phoneCode?.code === "+60") v = v.replace(/^0+/, "");
                                            setPhone(v);
                                        }}
                                        sx={{ ...fieldSx, flex: 1 }}
                                    />
                                </Box>
                                <TextField
                                    fullWidth
                                    multiline
                                    minRows={2}
                                    label={t("appointmentPage.form.notes")}
                                    placeholder={t("appointmentPage.form.notesPlaceholder")}
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    sx={fieldSx}
                                />

                                {/* Dark booking summary */}
                                <Box sx={{ mt: 3, p: 2.5, borderRadius: "14px", background: "linear-gradient(160deg, #2a2d34 0%, #1a1d24 70%)", color: "#fff" }}>
                                    <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, letterSpacing: "1.5px", textTransform: "uppercase", color: "#fff", mb: 2 }}>
                                        {t("appointmentPage.bookingSummary")}
                                    </Typography>
                                    {[
                                        [t("appointmentPage.summary.date"), fmtDate(selectedDate)],
                                        [t("appointmentPage.summary.time"), fmtTime(selectedTime)],
                                        [t("appointmentPage.summary.service"), selectedService?.name || t("appointmentPage.notSelected")],
                                    ].map(([label, value]) => (
                                        <Box key={label} sx={{ display: "flex", justifyContent: "space-between", gap: 2, mb: 1 }}>
                                            <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, color: "rgba(255,255,255,0.5)", flexShrink: 0 }}>
                                                {label}
                                            </Typography>
                                            <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, color: "#fff", textAlign: "right" }}>
                                                {value}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>
                            </CardContent>
                        </Card>

                        {error && (
                            <Alert severity="warning" sx={{ mb: 2, borderRadius: "12px" }}>
                                {t("appointmentPage.form.required")}
                            </Alert>
                        )}
                        {submitError && (
                            <Alert severity="error" sx={{ mb: 2, borderRadius: "12px" }}>
                                {submitError}
                            </Alert>
                        )}

                        {/* ═══ Confirm ═══ */}
                        <Button
                            fullWidth
                            onClick={handleSubmit}
                            disabled={submitting}
                            startIcon={
                                submitting ? (
                                    <CircularProgress size={18} sx={{ color: "#fff" }} />
                                ) : (
                                    <CheckCircleOutlineIcon />
                                )
                            }
                            sx={{
                                fontFamily: '"Inter", sans-serif',
                                fontWeight: 600,
                                fontSize: "0.8rem",
                                letterSpacing: "2px",
                                textTransform: "uppercase",
                                py: "1.1rem",
                                borderRadius: "16px",
                                background: "linear-gradient(160deg, #3a3d44 0%, #1a1d24 65%)",
                                color: "#fff",
                                boxShadow: "0 14px 34px rgba(0,0,0,0.25)",
                                transition: "all 0.3s ease",
                                "&:hover": { background: "linear-gradient(160deg, #2a2d34 0%, #000 65%)", transform: "translateY(-2px)" },
                                "&.Mui-disabled": { color: "rgba(255,255,255,0.7)", opacity: 0.8 },
                            }}
                        >
                            {submitting ? t("appointmentPage.sending") : t("appointmentPage.confirm")}
                        </Button>

                        <Typography sx={{ textAlign: "center", fontSize: "0.78rem", color: "#999", mt: 2, lineHeight: 1.7 }}>
                            {t("appointmentPage.form.footnote")}
                        </Typography>

                        <Divider sx={{ my: 3 }} />

                        <Box sx={{ textAlign: "center" }}>
                            <Button
                                component={RouterLink}
                                to="/services"
                                sx={{ textTransform: "none", color: "#666", fontFamily: '"Cormorant Garamond", serif', fontStyle: "italic", fontSize: "0.95rem", "&:hover": { color: INK, background: "transparent" } }}
                            >
                                {t("appointmentPage.form.browse")}
                            </Button>
                        </Box>
                    </>
                )}
            </Container>
        </Box>
    );
};

export default AppointmentPage;
